import { z } from "zod";

/**
 * Client-side boundary schema for logs (Tier 3, consumer side).
 *
 * Mirrors server/schemas/log-schema.js. The server normalizes logs it returns
 * from getLogsForStory, but the realtime `log-added` event is built straight
 * from a logger client's payload and reaches StoryProducer state WITHOUT going
 * through that normalizer. Parse such payloads here so a bad `tc`/`text` can
 * never crash LogCard (text.replace / tc.localeCompare).
 *
 * Coerce, never throw: every crash-critical field has a `.catch(default)`.
 */
export const ClientLogEntrySchema = z
  .object({
    id: z.string().catch(""),
    tc: z.string().catch(""),
    text: z.string().catch(""),
    author: z.string().catch(""),
    participants: z.array(z.unknown()).catch([]),
    cameras: z.array(z.unknown()).catch([]),
    tags: z.array(z.unknown()).catch([]),
    rating: z.number().catch(0),
    type: z.string().catch("log"),
  })
  .passthrough();

/** Parse one untrusted log payload into a guaranteed-safe shape. Never throws. */
export function parseLog(raw: unknown): Record<string, unknown> {
  const input = raw && typeof raw === "object" ? raw : {};
  return ClientLogEntrySchema.parse(input);
}

/** Parse an untrusted array of log payloads. Returns [] for non-arrays. */
export function parseLogs(raw: unknown): Record<string, unknown>[] {
  if (!Array.isArray(raw)) return [];
  return raw.map(parseLog);
}
